<template>
  <div
    class="flex flex-col items-center mx-4 mt-3 px-2"
    id="a4-receipt"
    :class="{
      'dir-ltr': $ctx.receiptLanguage == 'en',
      'dir-rtl': $ctx.receiptLanguage == 'ar'
    }"
  >
    <div class="flex flex-col items-center w-full a4-receipt-page" style="height: 100vh;">
      <!-- header info -->
      <div id="a4-receipt-header" class="flex flex-col w-full">
        <div class="flex mt-4 justify-between items-center w-full">
          <div class="mx-8 mt-3 pb-2">
            <div class="flex items-center gap-2 mb-1">
              <img src="@/assets/icons/receipt/map-marker.svg" width="16" height="16" class="shrink-0" />
              <span>{{ settingsStore.getValue('company_address') }}</span>
            </div>
            <div class="flex items-center gap-2 mb-1">
              <img src="@/assets/icons/receipt/phone.svg" width="16" height="16" class="shrink-0" />
              <span dir="ltr">{{ settingsStore.getValue('company_phone1') + (settingsStore.getValue('company_phone2')? ` - ${settingsStore.getValue('company_phone2')}`: '') }}</span>
            </div>
            <div class="flex items-center gap-6">
              <div v-if="settingsStore.getValue('company_email')" class="flex items-center gap-2">
                <img src="@/assets/icons/receipt/email.svg" width="16" height="16" class="shrink-0" />
                <span>{{ settingsStore.getValue('company_email') }}</span>
              </div>
              <div v-if="settingsStore.getValue('company_website')" class="flex items-center gap-2">
                <img src="@/assets/icons/receipt/web.svg" width="16" height="16" class="shrink-0" />
                <span>{{ settingsStore.getValue('company_website') }}</span>
              </div>
            </div>
          </div>

          <div class="mx-8">
            <img
              :src="settingsStore.getValue('receipt_logo') ? settingsStore.storagePath + '/' + settingsStore.getValue('receipt_logo'): '/images/logo.svg'"
              :width="settingsStore.getValue('receipt_logo_width')"
              :style="{'margin-top': settingsStore.getValue('receipt_logo_top_margin') + 'px', 'margin-right': settingsStore.getValue('receipt_logo_right_margin') + 'px'}"
              alt=""
            />
          </div>
        </div>

        <div class="flex flex-col w-full items-center">
          <div class="w-11/12 h-[3px] rounded-full"
              :style="{background: `linear-gradient(to right, transparent, ${settingsStore.getValue('receipt_branding_color') || '#585954'}, transparent)`}"></div>
        </div>
      </div>

      <div id="a4-receipt-body" class="w-full">
        <!-- title -->
        <div class="flex flex-col w-full mt-2 items-center">
          <h2 class="bg-gray-200 rounded py-1 px-16 font-bold mt-3 capitalize">
            <p>{{ receiptTranslate("Vendor Bill Receipt", "receipt.vendorBillReceipt") }}</p>
          </h2>
        </div>

        <!-- vendor bill info and barcode -->
        <div class="flex w-full justify-around">
          <div>
            <table class="w-full text-sm">
              <tr>
                <td><p>{{ receiptTranslate("Vendor", "receipt.vendor") }}</p></td>
                <td><p class="font-bold mx-5">{{ vendorBill?.vendor?.name || vendorBill?.vendorName }}</p></td>
              </tr>
              <tr v-if="vendorBill?.vendorBillNumber">
                <td><p>{{ receiptTranslate("Bill Number", "receipt.billNumber") }}</p></td>
                <td><p class="font-bold mx-5">{{ vendorBill?.vendorBillNumber }}</p></td>
              </tr>
              <tr v-if="vendorBill?.billDate">
                <td><p>{{ receiptTranslate("Bill Date", "receipt.billDate") }}</p></td>
                <td><p class="font-bold mx-5">{{ parseFormat(vendorBill?.billDate) }}</p></td>
              </tr>
              <tr v-if="vendorBill?.billDueDate">
                <td><p>{{ receiptTranslate("Due Date", "receipt.dueDate") }}</p></td>
                <td><p class="font-bold mx-5">{{ parseFormat(vendorBill?.billDueDate) }}</p></td>
              </tr>
              <tr v-if="vendorBill?.whatsFor">
                <td><p>{{ receiptTranslate("Purpose", "receipt.purpose") }}</p></td>
                <td><p class="font-bold mx-5">{{ vendorBill?.whatsFor }}</p></td>
              </tr>
              <tr v-if="vendorBill?.debitedAccount">
                <td><p>{{ receiptTranslate("Debited Account", "receipt.debitedAccount") }}</p></td>
                <td><p class="font-bold mx-5">{{ $ctx.locale === 'ar' ? vendorBill?.debitedAccount?.arName : vendorBill?.debitedAccount?.enName }}</p></td>
              </tr>
              <tr v-if="vendorBill?.user">
                <td><p>{{ receiptTranslate("Created By", "receipt.createdBy") }}</p></td>
                <td><p class="font-bold mx-5">{{ vendorBill?.user?.fullName }}</p></td>
              </tr>
              <tr v-if="vendorBill?.createdAt">
                <td><p>{{ receiptTranslate("Date & Time", "receipt.dateAndTime") }}</p></td>
                <td><p class="font-bold mx-5" dir="ltr">{{ parseFormat(vendorBill?.createdAt) }}</p></td>
              </tr>
              <tr v-if="vendorBill?.notes">
                <td><p>{{ receiptTranslate("Notes", "receipt.notes") }}</p></td>
                <td><p class="font-bold mx-5">{{ vendorBill?.notes }}</p></td>
              </tr>
            </table>
          </div>

          <div class="flex flex-col items-center">
            <div>
              <svg :id="`barcode-${vendorBill?.externalID}`" class="mt-3"></svg>
              <div class="flex items-center justify-center">
                <span class="font-bold mx-5" style="font-size: calc(20px * var(--receipt-font-scale, 1));">
                  {{ vendorBill?.externalID?.toString().slice(0, 5) }}-{{ vendorBill?.externalID?.toString().slice(5) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- financial summary -->
        <div class="text-sm flex flex-col w-full items-end mt-4">
          <div class="flex-col w-5/12 mr-10">
            <div class="px-4 w-full">
              <table class="payment-table" :class="{ 'ar-receipt': $ctx.receiptLanguage == 'ar' }">
                <tr>
                  <td>{{ receiptTranslate("Total Amount", "receipt.totalAmount") }}</td>
                  <td><p class="text-right">{{ formatDinero(vendorBill?.totalDueDinero) }}</p></td>
                </tr>
                <tr>
                  <td>{{ receiptTranslate("Total Paid", "receipt.totalPaid") }}</td>
                  <td><p class="text-right">{{ formatDinero(vendorBill?.totalPaidDinero) }}</p></td>
                </tr>
                <tr>
                  <td>{{ receiptTranslate("Balance", "receipt.balance") }}</td>
                  <td><p class="text-right font-bold">{{ formatDinero(vendorBill?.totalBalanceDinero) }}</p></td>
                </tr>
              </table>
            </div>
          </div>
        </div>

        <!-- transactions list -->
        <div v-if="transactions.length > 0" class="mt-6">
          <div class="flex flex-col w-full items-center">
            <h3 class="bg-gray-100 rounded py-1 px-2 font-bold">
              {{ receiptTranslate("Payment Transactions", "receipt.paymentTransactions") }}
            </h3>
          </div>

          <div class="text-sm flex flex-col w-full items-end mt-2">
            <div class="flex-col w-10/12 mr-10">
              <div class="px-4 w-full" v-for="transaction in transactions" :key="transaction.id">
                <div class="border-t border-gray-300 pt-2 mt-2">
                  <table class="payment-table w-full" :class="{ 'ar-receipt': $ctx.receiptLanguage == 'ar' }">
                    <tr>
                      <td colspan="2">
                        <p class="font-bold">
                          {{ receiptTranslate("Payment ID", "receipt.paymentID") }}: {{ transaction.externalID }}
                        </p>
                      </td>
                    </tr>
                    <tr>
                      <td>{{ receiptTranslate("Date", "receipt.date") }}</td>
                      <td><p class="text-right">{{ parseFormat(transaction.createdAt) }}</p></td>
                    </tr>
                    <tr>
                      <td>{{ receiptTranslate("Amount", "receipt.amount") }}</td>
                      <td><p class="text-right">{{ formatDinero(transaction.amountDinero) }}</p></td>
                    </tr>
                    <tr v-if="transaction.description">
                      <td>{{ receiptTranslate("Notes", "receipt.notes") }}</td>
                      <td><p class="text-right">{{ transaction.description }}</p></td>
                    </tr>
                    <tr v-if="transaction.user && settingsStore.getValue('hide_cashier_name_in_receipt') != 'true'">
                      <td>{{ receiptTranslate("Cashier", "receipt.cashier") }}</td>
                      <td><p class="text-right">{{ transaction.user.fullName }}</p></td>
                    </tr>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        id="a4-receipt-footer"
        v-if="settingsStore.getValue('show_receipts_thecky_footer') == 'true'"
        class="flex justify-center self-center w-full mt-auto"
      >
        <div class="flex items-center justify-between py-4 px-4 mt-2" style="bottom: 0px; width: 95%; border-top: 2px solid #BDC7D6">
          <p><img src="@/assets/images/thecky-qr.png" style="height: 25px;"/></p>
          <p><img src="@/assets/images/footer.svg" style="height: 25px;"/></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { parseFormat } from "@/utils/date";
import { useI18n } from "vue-i18n";
import { formatDinero } from "@/utils/money";
import { Transaction } from "@/models/accounting/Transaction";
import JsBarcode from "jsbarcode";
import { useSettingsStore } from "@/stores/settingsStore";
import VendorBill, { vendorBillI18nMessages } from "@/models/vendor/VendorBill";
import { receiptPrintCSS } from "@/utils/receiptPrint";

export default defineComponent({
  components: {},

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          ...vendorBillI18nMessages.en,
          receipt: {
            vendorBillReceipt: 'Vendor Bill Receipt',
            vendor: 'Vendor',
            billNumber: 'Bill Number',
            billDate: 'Bill Date',
            dueDate: 'Due Date',
            purpose: 'Purpose',
            debitedAccount: 'Debited Account',
            createdBy: 'Created By',
            dateAndTime: 'Date & Time',
            notes: 'Notes',
            totalAmount: 'Total Amount',
            totalPaid: 'Total Paid',
            balance: 'Balance',
            paymentTransactions: 'Payment Transactions',
            paymentID: 'Payment ID',
            date: 'Date',
            amount: 'Amount',
            cashier: 'Cashier'
          }
        },
        ar: {
          ...vendorBillI18nMessages.ar,
          receipt: {
            vendorBillReceipt: 'فاتورة مورد',
            vendor: 'المورد',
            billNumber: 'رقم الفاتورة',
            billDate: 'تاريخ الفاتورة',
            dueDate: 'تاريخ الاستحقاق',
            purpose: 'الغرض',
            debitedAccount: 'حساب المدين',
            createdBy: 'انشئ من قبل',
            dateAndTime: 'التاريخ والوقت',
            notes: 'ملاحظات',
            totalAmount: 'المبلغ الإجمالي',
            totalPaid: 'المبلغ المدفوع',
            balance: 'الرصيد',
            paymentTransactions: 'حركات الدفع',
            paymentID: 'رقم الدفعة',
            date: 'التاريخ',
            amount: 'المبلغ',
            cashier: 'الكاشير'
          }
        }
      }
    });

    const settingsStore = useSettingsStore();
    return { t, settingsStore };
  },

  props: {
    vendorBill: {
      type: Object as PropType<VendorBill>,
      required: true
    },
    transactions: {
      type: Array as PropType<Array<Transaction>>,
      required: true
    }
  },

  mounted() {
    setTimeout(() => {
      if (this.vendorBill?.externalID) {
        JsBarcode(
          `#barcode-${this.vendorBill.externalID}`,
          String(this.vendorBill.externalID),
          {
            height: 50,
            displayValue: false
          }
        );
      }
    }, 100);
  },

  created() {
    this.addPrintCSS();
  },

  beforeUnmount() {
    this.removePrintCSS();
  },

  methods: {
    parseFormat,
    formatDinero,
    addPrintCSS() {
      const style = document.createElement("style");
      style.setAttribute("id", "print-css");
      style.textContent = receiptPrintCSS(this.$ctx.getReceiptSize());
      document.head.appendChild(style);
    },
    removePrintCSS() {
      const style = document.querySelector("#print-css");
      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      }
    },
    receiptTranslate(value: string, translateKey: string): string {
      return this.$ctx.receiptLanguage == "en" ? value : this.t(translateKey, this.$ctx.receiptLanguage);
    }
  }
});
</script>

<style>
.thecky-site {
  color: #000;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 3px;
}

#a4-receipt {
  width: 210mm;
  overflow: scroll;
  background-color: #fff;
}

.payment-table {
  width: 100%;
  border-collapse: collapse;
}

.payment-table td {
  padding: 4px;
}

@media print {
  body * {
    page-break-after: avoid;
    visibility: hidden;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  #a4-receipt,
  #a4-receipt * {
    visibility: visible;
  }

  #a4-receipt {
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
    margin: 0cm;
  }

  #a4-receipt-header {
    top: 0;
  }

  #a4-receipt-body {
    position: relative;
  }

  p,
  div {
    page-break-inside: avoid;
  }
}
</style>
